import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ContentHeader, Field, Modal, QueryStatus, useToast } from "@cx/ui";
import type { TenantUsersView, UserCreateInput } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";

type Draft = UserCreateInput & { confirm: string };
const EMPTY: Draft = { name: "", email: "", roleId: "", password: "", confirm: "" };

/** Primeiro problema do formulário, para mostrar antes de enviar; null quando está tudo certo. */
export function newUserProblem(d: Draft): string | null {
  if (d.name.trim().length < 2) return "Informe o nome.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) return "Informe um e-mail válido.";
  if (!d.roleId) return "Escolha o perfil.";
  if (d.password.length < 12) return "A senha inicial precisa ter 12 caracteres ou mais.";
  if (d.password !== d.confirm) return "As senhas não conferem.";
  return null;
}

/** Preferências › Usuários: quem tem acesso à conta e com qual perfil; o administrador adiciona pessoas. */
export function UsersPage(): ReactElement {
  const { me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const list = useQuery({ queryKey: ["users"], queryFn: () => api<TenantUsersView>("/users") });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: ({ confirm: _confirm, ...body }: Draft) =>
      api("/users", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      toast("success", "Usuário adicionado. Passe a senha inicial para a pessoa entrar.");
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => setProblem(errorMessage(e)),
  });
  const set = (k: keyof Draft) => (e: { target: { value: string } }) =>
    setDraft((d) => (d ? { ...d, [k]: e.target.value } : d));
  const submit = () => {
    if (!draft) return;
    const p = newUserProblem(draft);
    setProblem(p);
    if (!p) create.mutate({ ...draft, name: draft.name.trim(), email: draft.email.trim() });
  };
  return (
    <>
      <ContentHeader
        title="Usuários"
        breadcrumb={["Preferências", "Usuários"]}
        actions={
          can(perms, "users", "create") && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!list.isSuccess}
              onClick={() => {
                setProblem(null);
                setDraft(EMPTY);
              }}
            >
              Adicionar usuário
            </button>
          )
        }
      />
      <div className="card">
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.data && (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Perfil</th>
                  <th>Último acesso</th>
                </tr>
              </thead>
              <tbody>
                {list.data.users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.roleName}</td>
                    <td>{u.lastLoginAt ? dateTime(u.lastLoginAt) : "Nunca entrou"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Modal
        open={draft !== null}
        title="Adicionar usuário"
        onClose={() => setDraft(null)}
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setDraft(null)}>
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" disabled={create.isPending} onClick={submit}>
              {create.isPending ? "Adicionando…" : "Adicionar"}
            </button>
          </>
        }
      >
        {draft && (
          <div className="row">
            {problem && (
              <div className="col-12">
                <div className="alert alert-danger" role="alert">
                  {problem}
                </div>
              </div>
            )}
            <Field label="Nome" required className="col-12">
              <input className="form-control" value={draft.name} onChange={set("name")} data-autofocus />
            </Field>
            <Field label="E-mail" required className="col-12">
              <input type="email" className="form-control" value={draft.email} onChange={set("email")} />
            </Field>
            <Field label="Perfil" required className="col-12">
              <select className="form-select" value={draft.roleId} onChange={set("roleId")}>
                <option value="">Escolha…</option>
                {list.data?.roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Senha inicial" required hint="12 caracteres ou mais" className="col-md-6">
              <input
                type="password"
                autoComplete="new-password"
                className="form-control"
                value={draft.password}
                onChange={set("password")}
              />
            </Field>
            <Field label="Repita a senha" required className="col-md-6">
              <input
                type="password"
                autoComplete="new-password"
                className="form-control"
                value={draft.confirm}
                onChange={set("confirm")}
              />
            </Field>
          </div>
        )}
      </Modal>
    </>
  );
}
